import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EMPTY_OBJECT } from 'utility/fieldHelpers';
import UUID from 'utility/UUID';

export default function useModal({
  handleError,
  handleHide,
  handleSubmit,
  closeOnSubmit = true,
  processHideOnSubmit = true,
  initialValue = false,
  override,
  setOverride,
} = {}) {
  const [show, setShow] = useState(initialValue);
  const [networkError, setNetworkError] = useState(EMPTY_OBJECT);
  const hideFn = useRef(handleHide);
  const submitFn = useRef(handleSubmit);
  const errorFn = useRef(handleError);
  const closeOnSubmitBool = useRef(closeOnSubmit);
  const processHideOnSubmitBool = useRef(processHideOnSubmit);

  useEffect(() => {
    hideFn.current = handleHide;
    submitFn.current = handleSubmit;
    errorFn.current = handleError;
  });

  useEffect(() => {
    if (typeof override === 'boolean') {
      setShow(override);
    }
  }, [override, setShow]);

  const onHide = useCallback(() => {
    if (hideFn.current) {
      hideFn.current();
    }
    setNetworkError(EMPTY_OBJECT);
    setOverride?.(false);
    return setShow(false);
  }, [setOverride]);

  const showModal = useCallback(() => {
    setNetworkError(EMPTY_OBJECT);
    setShow(true);
  }, []);

  const onConfirm = useCallback(
    async (...args) => {
      let maybeError;
      let returnValue;
      try {
        if (submitFn.current) {
          const mutationResult = await submitFn.current(...args);
          returnValue = { success: true, mutationResult };
        }
      } catch (err) {
        maybeError = err;
      }
      if (maybeError && errorFn.current) {
        const { errors = {} } =
          (maybeError.isAxiosError && maybeError?.response?.data) || {};
        setNetworkError(errors);
        errorFn.current(maybeError);
        returnValue = { success: false, errors };
      } else if (closeOnSubmitBool.current) {
        if (processHideOnSubmitBool.current) {
          onHide();
        } else {
          setShow(false);
        }
      }

      return returnValue;
    },
    [onHide]
  );

  const uuid = useMemo(() => UUID.generate(), []);
  const key = `${show}-${uuid}`;

  return [
    {
      key,
      onConfirm,
      onHide,
      show,
      networkError,
    },
    {
      onClick: showModal,
    },
    // controller interface, for when modal logic is used piecemeal, not spread
    {
      // listing mostly for completeness; seems dubious you'd ever need / want to submit a modal other than by clicking own submission button
      confirm: onConfirm,
      showing: show,
      setShow,
      show: showModal,
      hide: onHide,
      // Not ideal, but sometimes the modal we need to manage isn't collocated with a useModal call, but we need to get a key to it anyway
      // see pages/ContactDetail/ActionBar, usage of AutomationActions (which contains a modal)
      key,
    },
  ];
}
